import React, { useEffect, useState } from 'react';
import { Button, Stack, Text, List, Icon } from '@iqueue/iq-ui';
import Modal from '../ui/Modal';
import { api } from '../../lib/api';
import { useSlowWait } from '../../lib/useSlowWait';

const fmtSize = (b) => (b >= 1e6 ? `${(b / 1e6).toFixed(1)} МБ` : b >= 1e3 ? `${Math.round(b / 1e3)} КБ` : `${b || 0} Б`);
const when = (ts) => new Date(ts).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });

/** Глиф по типу файла: строка должна говорить, что это, до открытия. */
function fileIcon(mime, name) {
  const m = String(mime || '');
  if (m.startsWith('image/')) return 'image';
  if (m.startsWith('video/')) return 'movie';
  if (m.startsWith('audio/')) return 'graphic_eq';
  if (m.includes('zip') || m.includes('tar') || m.includes('compressed')) return 'folder_zip';
  if (m.includes('pdf')) return 'picture_as_pdf';
  if (/\.(md|txt|log)$/i.test(name || '')) return 'description';
  if (/\.(js|ts|jsx|tsx|py|sh|json|ya?ml)$/i.test(name || '')) return 'terminal';
  return 'draft';
}

/**
 * Файлы комнаты: всё, что в неё загрузили или что произвели запуски агентов
 * (`GET /api/rooms/:id/artifacts`). Строка — ссылка: `href` на выдачу артефакта,
 * которая сама отдаёт файл с Content-Disposition, поэтому скачивание не требует
 * ни своего обработчика, ни кнопки.
 */
export default function RoomFilesDialog({ room, onClose }) {
  const [files, setFiles] = useState(null);
  const [error, setError] = useState('');
  const waiting = useSlowWait(!files && !error);

  useEffect(() => {
    let alive = true;
    api.roomArtifacts(room.id)
      .then(r => { if (alive) setFiles(r.artifacts || []); })
      .catch(e => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, [room.id]);

  return (
    <Modal
      open
      onClose={onClose}
      title={`Файлы — ${room.name || room.id}`}
      actions={<Button variant="text" onClick={onClose}>Закрыть</Button>}
    >
      <Stack gap={200}>
        {error && <Text variant="body-small">{error}</Text>}
        {!files && !error && (waiting ? <Text variant="body-medium" muted>Ищу файлы…</Text> : null)}
        {files && files.length === 0 && (
          <Text variant="body-medium" muted>В этой комнате пока ничего не сохранено.</Text>
        )}
        {files && files.length > 0 && (
          <List aria-label="Файлы комнаты">
            {files.map(f => (
              <List.Item
                key={f.id}
                leading={<Icon name={fileIcon(f.mime_type, f.original_name)} />}
                href={api.artifactUrl(f.id)}
                supporting={`${fmtSize(f.size)} · ${f.created_by || 'агент'} · ${when(f.created_at)}`}
                trailing={<Icon name="download" size="s" />}
              >
                {f.original_name}
              </List.Item>
            ))}
          </List>
        )}
      </Stack>
    </Modal>
  );
}
